import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useIntl, FormattedMessage } from "gatsby-plugin-intl";
import debounce from "lodash.debounce";
import _get from "lodash.get";
import * as queryString from "query-string";
import Layout from "components/Layout";
import SEO from "components/SEO";
import GoogleMap from "components/GoogleMap";
import AgenciesList from "components/AgenciesList";
import AgencyCard from "components/AgenciesList/AgencyCard";
import ReactSelect from "components/SelectField/ReactSelect";
import CheckboxOption from "components/SelectField/CheckboxOption";
import { useBranches } from "components/Branches/hooks";
import { useCities, useNearestCity } from "hooks/city";
import { useCriteriaStore } from "hooks/criteriaStore";

/**
 * we default to Casablanca as default city in case if the user has entered the page without ?cityId=<ID> param
 * and no geolocation data provided (not activated or an error occurred)
 */
const DEFAULT_CITY_ID = 41; // Casablanca

const getCityIdFromURL = () => {
  const isBrowser = () => typeof window !== "undefined";
  if (isBrowser() && window.location.search) {
    const { city: cityId } = queryString.parse(window.location.search);
    return cityId || null;
  }
  return null;
};
const getQuartierFromURL = () => {
  const isBrowser = () => typeof window !== "undefined";
  if (isBrowser() && window.location.search) {
    const { quartier } = queryString.parse(window.location.search);
    return quartier || null;
  }
  return null;
};
const AgencyPage = () => {
  const { formatMessage } = useIntl();

  const defaultPaginationOptions = {
    pageSize: 10,
    page: 0,
    orderBy: "id",
    direction: "ASC",
  };
  const [fetchCities, { data: citiesData, error: citiesError }] = useCities();
  const [
    fetchBranches,
    {
      data: branchesData,
      // loading: branchesLoading     // use this to display a loader
    },
  ] = useBranches();

  const criteriaStore = useCriteriaStore({ ...defaultPaginationOptions });
  const [selectedFilters, setSelectedFilters] = useState(null);
  const [selectedAgencyCard, setSelectedAgencyCard] = useState(null);
  const [currentCity, setCurrentCity] = useState(null);
  const [quartier, setQuartier] = useState(null);
  const { nearestCity } = useNearestCity();

  const queryParamCity = useMemo(() => {
    const cityId = getCityIdFromURL();
    setQuartier(getQuartierFromURL());
    const foundCity = citiesData?.content?.find(
      e => Number(e.id) === (Number(cityId) || DEFAULT_CITY_ID)
    );

    if (foundCity) {
      return {
        ...foundCity,
        isDefault: foundCity.id === DEFAULT_CITY_ID,
      };
    }

    return null;
  }, [citiesData]);

  useEffect(() => {
    /* READ COMMENT ABOVE */
    // TODO: Refactor
    if (queryParamCity?.isDefault && nearestCity) {
      setCurrentCity({
        ...nearestCity,
        geolocated: true,
      });
    } else if (queryParamCity?.isDefault && !nearestCity) {
      setCurrentCity(queryParamCity);
    }

    if (!queryParamCity?.isDefault) {
      setCurrentCity(queryParamCity);
    }
  }, [queryParamCity, nearestCity, citiesData]);

  useEffect(() => {
    fetchCities({
      query: "pageSize=1000",
    });
  }, []);

  useEffect(() => {
    if (currentCity) {
      const query = {
        quartier,
        pageSize: 1000,
        cityId: currentCity.id,
      };
      if (selectedFilters) {
        selectedFilters.forEach(filter => {
          query[filter.value] = true;
        });
      }
      fetchBranches({
        query: queryString.stringify(query),
      });
    }
  }, [currentCity, selectedFilters, quartier]);

  const handleChangeDebounced = useCallback(
    debounce(value => {
      criteriaStore.setCriteria({ name: value });
    }, 500),
    []
  );

  return (
    <Layout>
      <SEO title={formatMessage({ id: "navigation.agencies" })} />
      <div className="mt-32 flex flex-col-reverse lg:flex-row">
        <div className="w-full lg:w-1/2 py-8 px-16">
          <div className="text-p-title text-black font-bold">
            <FormattedMessage id="branches.page-title" />
          </div>
          <div className="text-p-title text-primary font-bold capitalize">
            {currentCity?.name?.toLowerCase()}
          </div>

          <div className="text-p-content mb-8">
            {branchesData.totalElements
              ? `+${branchesData.totalElements} `
              : "0 "}
            <FormattedMessage id="branches.agence" />
          </div>

          <div className="flex flex-col sm:flex-row justify-between">
            <ReactSelect
              className="react-select-container"
              classNamePrefix="react-select"
              placeholder={formatMessage({ id: "general.city" })}
              value={
                currentCity
                  ? { label: currentCity.name, value: currentCity }
                  : {}
              }
              onChange={option => {
                setCurrentCity(option.value);
              }}
              onInputChange={inputVal => handleChangeDebounced(inputVal)}
              options={
                citiesData && citiesData.content
                  ? citiesData.content.map(option => ({
                      label: option.name,
                      value: option,
                    }))
                  : []
              }
            />
            <input
              name="quartier"
              type="text"
              placeholder="Quartier"
              onChange={event => setQuartier(event.target.value)}
              className="agency-quartier"
            />

            <ReactSelect
              className="react-select-container"
              classNamePrefix="react-select"
              isMulti
              controlShouldRenderValue={false}
              closeMenuOnSelect={false}
              hideSelectedOptions={false}
              placeholder={formatMessage({ id: "general.filter" })}
              value={selectedFilters}
              onChange={options => setSelectedFilters(options)}
              components={{ Option: CheckboxOption }}
              options={[
                {
                  value: "nationalTransfer",
                  label: formatMessage({ id: "branches.filter.national" }),
                },
                {
                  value: "internationalTransfer",
                  label: formatMessage({
                    id: "branches.filter.international",
                  }),
                },
                {
                  value: "encaissement",
                  label: formatMessage({
                    id: "branches.filter.encaissement",
                  }),
                },
              ]}
              styles={{
                option: provided => ({
                  ...provided,
                  backgroundColor: "white",
                  color: "black",
                }),
              }}
            />
          </div>

          {citiesError && (
            <div className="text-sm text-red-600 bg-red-200 px-2 py-2 mt-2 rounded-sm">
              <FormattedMessage id="errors.citiesLoading" />
            </div>
          )}

          <div>
            <AgenciesList className="mt-12">
              {branchesData?.content?.map(branch => {
                const {
                  name,
                  address,
                  fixeNumber,
                  mobileNumber,
                  humanReadable,
                  longitude,
                  latitude,
                  id,
                } = branch;
                return (
                  <AgencyCard
                    data={{
                      name,
                      address,
                      fixNumber: fixeNumber,
                      mobileNumber,
                      distance: humanReadable ? humanReadable.split(" ") : "",
                      lat: latitude,
                      lng: longitude,
                      id,
                    }}
                    key={id}
                    isHighlighted={id === selectedAgencyCard}
                  />
                );
              })}
            </AgenciesList>
          </div>
        </div>
        <div className="w-full lg:w-1/2">
          <GoogleMap
            centerMap={{
              lat: (currentCity && currentCity.latitude) || 28.51713,
              lng: (currentCity && currentCity.longitude) || -10.09925,
            }}
            zoom={12}
            indicators={_get(branchesData, "content", [])}
            gkey={currentCity && currentCity.name}
            highlightAgencyCard={id => setSelectedAgencyCard(id)}
          />
        </div>
      </div>
    </Layout>
  );
};

export default AgencyPage;
